import {useState, useEffect} from 'react';

import {BoxType, SurveySlug} from 'client/common/types';

import {useBoxType} from 'client/common/hooks/box';
import {useIsLauncherRole} from 'client/common/hooks/user';
import {useSurveyPackageInfo} from 'client/common/hooks/survey';

import {getSurveyBindedPackage} from 'client/common/utils/api-client/constructor';

interface UsePackageBindingParams {
    slug: SurveySlug;
    boxType?: BoxType;
}

export function usePackageBinding({boxType, slug}: UsePackageBindingParams) {
    const [isBinded, setIsBinded] = useState(false);
    const [isFetching, setIsFetching] = useState(true);

    const isLauncherRole = useIsLauncherRole();

    useEffect(() => {
        if (!isLauncherRole || boxType !== 'creative') {
            setIsFetching(false);

            return;
        }

        async function getBindedPackage() {
            const pkg = await getSurveyBindedPackage({slug});

            setIsFetching(false);
            setIsBinded(Boolean(pkg.data?.packageId));
        }

        void getBindedPackage();
    }, [slug, isLauncherRole, boxType, setIsFetching, setIsBinded]);

    return {
        isBinded,
        isFetching
    };
}

export function useCanShowPackagePay(slug: SurveySlug) {
    const boxType = useBoxType(slug);
    const isPayByPackageAvailable = useIsPayByPackageAvailable(slug);

    const isLauncherRole = useIsLauncherRole();
    const packageBinding = usePackageBinding({boxType, slug});

    const packageInfo = useSurveyPackageInfo(slug);
    const packagesLeft = packageInfo?.data?.count ?? 0;

    const isEnoughPackagesLeft = isLauncherRole ? !packageBinding.isFetching : packagesLeft > 0;
    const showPackagePay = isPayByPackageAvailable && isEnoughPackagesLeft;

    return showPackagePay;
}

const PACKAGE_KEY = 'PACKAGE';

export function getPackageKey(packageId?: number) {
    return [PACKAGE_KEY, {packageId}] as const;
}

export const BOX_TYPES_WITH_PACKAGE_PAY = ['creative', 'bordomatik'];

export function useIsPayByPackageAvailable(slug: SurveySlug) {
    const boxType = useBoxType(slug);

    return BOX_TYPES_WITH_PACKAGE_PAY.includes(boxType!);
}
